import { Router } from '@angular/router';
import { Observable, from, of, timer } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

/** How often, and how many times, the section is looked for after the navigation (about one second in all). */
const FIND_EVERY_MS = 50;
const FIND_ATTEMPTS = 21;
/** The page above the section is still settling (images, the plan list arriving); the position is checked once more after this. */
const SETTLE_MS = 700;
/** Where the section should end up: just under the sticky header. Further out than the tolerance and it is scrolled again. */
const HEADER_OFFSET_PX = 84;
const OFFSET_TOLERANCE_PX = 24;

/**
 * Takes the visitor to a section of a page, whether or not they are already on that page.
 *
 * ⚠ WHY THIS EXISTS (owner, 2026-09-17: "Get your LifeCard button not working"). The header button was
 * `routerLink="/plans"` and /plans is the page the site opens on, so pressing it navigated to the URL the
 * visitor was already at — Angular ignores a same-URL navigation, and nothing moved. A plain
 * `href="#plans"` is no better: with `<base href="/">` it resolves to `/#plans`, a full page reload that
 * lands at the top. So: already there → scroll; elsewhere → navigate, then scroll once the page exists.
 *
 * ⚠ IT RETURNS AN OBSERVABLE AND LOOKS ONLY INSIDE `root` (review MR !1 S17). The waits are RxJS timers, so
 * the caller ends them with its own lifetime (`takeUntilDestroyed`) instead of leaving bare `setTimeout`s that
 * outlive the screen; and the section is looked up under the caller's own host element (the shell, whose
 * `<router-outlet>` holds the page), not anywhere in the document.
 */
export function scrollToSection(router: Router, path: string, sectionId: string, root: ParentNode = document): Observable<void> {
  const find = (): HTMLElement | null => root.querySelector<HTMLElement>(`#${CSS.escape(sectionId)}`);
  const here = router.url.split(/[?#]/)[0] === path;
  if (here) {
    find()?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return of(undefined);
  }
  return from(router.navigate([path])).pipe(
    // The lazy page renders its section synchronously, but give it a frame or two to exist.
    switchMap(() => timer(0, FIND_EVERY_MS).pipe(take(FIND_ATTEMPTS), map(find), filter((target): target is HTMLElement => !!target), take(1))),
    tap((target) => target.scrollIntoView({ behavior: 'smooth', block: 'start' })),
    // …and correct the position once more after the page above it has had a moment, or the visitor lands part-way down the hero.
    switchMap(() => timer(SETTLE_MS)),
    tap(() => {
      const target = find();
      const top = target?.getBoundingClientRect().top ?? 0;
      if (target && Math.abs(top - HEADER_OFFSET_PX) > OFFSET_TOLERANCE_PX) {
        target.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }),
    map(() => undefined),
  );
}
